import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { sendEmail } from '../services/emailService';
import { 
  TrendingUp, 
  Shield, 
  Clock, 
  Star, 
  DollarSign, 
  Home, 
  Users, 
  CheckCircle,
  ArrowRight,
  Calculator,
  Phone,
  Mail
} from 'lucide-react';

const Hosts = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyType: '',
    location: '',
    bedrooms: '',
    currentRevenue: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Show loading message
    toast.loading('Sending your partnership inquiry...');
    
    try {
      // Create enhanced form data for email
      const emailFormData = {
        ...formData,
        subject: 'Host Partnership Inquiry',
        inquiryType: 'Property Partnership',
        message: `${formData.message}\n\nProperty Details:\n- Type: ${formData.propertyType}\n- Location: ${formData.location}\n- Bedrooms: ${formData.bedrooms}\n- Current Revenue: ${formData.currentRevenue}`
      };
      
      const result = await sendEmail(emailFormData, 'Host Partnership', 'Hosts Page');
      
      if (result.success) {
        toast.success('Thank you! Our team will contact you within 24 hours.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          propertyType: '',
          location: '',
          bedrooms: '',
          currentRevenue: '',
          message: ''
        });
      } else {
        toast.error('Failed to send inquiry. Please try again or contact us directly.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to send inquiry. Please try again or contact us directly.');
    }
  };

  const benefits = [
    {
      icon: TrendingUp,
      title: "Revenue Optimization",
      description: "Advanced pricing strategies and market analysis to maximize your property's earning potential"
    },
    {
      icon: Shield,
      title: "Complete Protection",
      description: "Comprehensive insurance coverage and thorough guest verification for absolute peace of mind"
    },
    {
      icon: Clock,
      title: "Elite Concierge",
      description: "Round-the-clock luxury guest services and premium hospitality management"
    },
    {
      icon: Star,
      title: "Five-Star Standards",
      description: "White-glove service standards ensuring exceptional guest experiences and reviews"
    }
  ];

  const services = [
    "Strategic Revenue Management",
    "Professional Photography & Staging",
    "Global Platform Optimization",
    "Guest Curation & Communication",
    "Premium Housekeeping Services",
    "Maintenance & Concierge Coordination",
    "Advanced Analytics & Reporting",
    "Legal Compliance & Risk Management"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1606836591695-4d58a73eba1e)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
        </div>
        
        <div className="relative z-10 flex items-center h-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h1 className="text-6xl font-extralight text-white mb-8 leading-tight">
                  Elevate Your Property
                  <span className="block font-light text-white/90">Into Luxury</span>
                </h1>
                <p className="text-xl text-white/90 mb-12 font-light leading-relaxed">
                  Partner with hospitality experts who understand the art of luxury service. 
                  Join our curated network of premium properties.
                </p>
                <div className="flex flex-col sm:flex-row gap-6">
                  <Button size="lg" className="bg-white text-black hover:bg-gray-100 font-light px-10 py-6">
                    <Calculator className="mr-3 h-5 w-5" />
                    Revenue Calculator
                  </Button>
                  <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-black font-light px-10 py-6">
                    <Phone className="mr-3 h-5 w-5" />
                    Schedule Consultation
                  </Button>
                </div>
              </div>
              <div className="lg:pl-12">
                <Card className="bg-white/10 backdrop-blur-md border-white/20 shadow-2xl">
                  <CardContent className="p-10">
                    <h3 className="text-3xl font-light text-white mb-8">Revenue Potential</h3>
                    <div className="space-y-6">
                      <div className="flex justify-between items-center">
                        <span className="text-white/90 font-light">Luxury Apartment</span>
                        <span className="text-3xl font-light text-white">$4,200/mo</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-white/90 font-light">Premium Villa</span>
                        <span className="text-3xl font-light text-white">$8,500/mo</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-white/90 font-light">Executive Suite</span>
                        <span className="text-3xl font-light text-white">$12,800/mo</span>
                      </div>
                    </div>
                    <p className="text-sm text-white/70 mt-8 font-light">
                      *Revenue projections based on premium market positioning and optimal management
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-light text-gray-900 mb-6">
              The Vantage Points Advantage
            </h2>
            <p className="text-xl text-gray-600 font-light max-w-3xl mx-auto leading-relaxed">
              Experience the difference of working with hospitality professionals who understand luxury.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <Card key={index} className="border-0 shadow-sm hover:shadow-xl transition-all duration-500 h-full">
                  <CardContent className="p-10">
                    <div className="flex items-start space-x-6">
                      <div className="bg-gray-100 p-4 rounded-lg">
                        <IconComponent className="h-8 w-8 text-gray-800" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-light text-gray-900 mb-4">{benefit.title}</h3>
                        <p className="text-gray-600 font-light leading-relaxed">{benefit.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-light text-gray-900 mb-6">
              Complete Hospitality Management
            </h2>
            <p className="text-xl text-gray-600 font-light">
              Every aspect handled with meticulous attention to detail.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="text-center border-0 shadow-sm hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8">
                  <CheckCircle className="h-10 w-10 text-gray-600 mx-auto mb-6" />
                  <p className="font-light text-gray-900 leading-relaxed">{service}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-light text-gray-900 mb-6">
              Begin Your Journey
            </h2>
            <p className="text-xl text-gray-600 font-light">
              Schedule a personalized consultation to discover your property's potential.
            </p>
          </div>

          <Card className="shadow-2xl border-0">
            <CardHeader className="bg-black text-white text-center rounded-t-lg">
              <CardTitle className="text-2xl font-light">
                <Mail className="inline-block mr-3 h-6 w-6" />
                Property Assessment Request
              </CardTitle>
            </CardHeader>
            <CardContent className="p-12">
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-3">
                      Full Name *
                    </label>
                    <Input
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Enter your full name"
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-3">
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="your.email@example.com"
                      className="h-12"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-3">
                      Phone Number
                    </label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+1 (555) 123-4567"
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-3">
                      Property Type *
                    </label>
                    <Select value={formData.propertyType} onValueChange={(value) => setFormData({...formData, propertyType: value})}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select property type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="apartment">Luxury Apartment</SelectItem>
                        <SelectItem value="villa">Private Villa</SelectItem>
                        <SelectItem value="penthouse">Penthouse Suite</SelectItem>
                        <SelectItem value="estate">Private Estate</SelectItem>
                        <SelectItem value="other">Other Premium Property</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-3">
                      Location *
                    </label>
                    <Input
                      required
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      placeholder="City, Country"
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-3">
                      Bedrooms
                    </label>
                    <Select value={formData.bedrooms} onValueChange={(value) => setFormData({...formData, bedrooms: value})}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="# of bedrooms" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="studio">Studio</SelectItem>
                        <SelectItem value="1">1 Bedroom</SelectItem>
                        <SelectItem value="2">2 Bedrooms</SelectItem>
                        <SelectItem value="3">3 Bedrooms</SelectItem>
                        <SelectItem value="4">4 Bedrooms</SelectItem>
                        <SelectItem value="5+">5+ Bedrooms</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-3">
                      Current Revenue (Optional)
                    </label>
                    <Input
                      value={formData.currentRevenue}
                      onChange={(e) => setFormData({...formData, currentRevenue: e.target.value})}
                      placeholder="$5,000/month"
                      className="h-12"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-light text-gray-700 mb-3">
                    Tell Us About Your Vision
                  </label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    placeholder="Share your property goals, investment objectives, or any specific requirements..."
                    rows={5}
                    className="resize-none"
                  />
                </div>

                <Button type="submit" size="lg" className="w-full bg-black hover:bg-gray-800 text-white font-light py-6">
                  Request Consultation
                  <ArrowRight className="ml-3 h-5 w-5" />
                </Button>
              </form>

              <div className="mt-8 text-center">
                <p className="text-sm text-gray-600 font-light">
                  Our hospitality specialists will contact you within 24 hours to discuss your property's potential.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Hosts;